import { useNavigate } from 'react-router-dom';
import { AlertTriangle, DoorOpen, Edit2, Trash2 } from 'lucide-react';
import type { Device, Unit } from '@/types/api';
import { Badge } from '@/components/ui/badge';
import { cn, formatRelativeTime } from '@/lib/utils';
import { hasFreshTransmission } from '@/features/devices/utils/has-fresh-transmission';
import { deviceTypeLabel, formatUnitOfMeasureSuffix } from '@/features/devices/utils/salt-config';
import { deviceDetailPath } from '@/features/devices/utils/device-navigation';

interface Props {
  unit: Unit;
  devices: Device[];
  propertyId: string;
  canWrite: boolean;
  onEdit: (unit: Unit) => void;
  onDelete: (unit: Unit) => void;
}

// Card-style row for the Units grid (mirrors PropertyDeviceCard's
// aesthetic so the toggle between Devices and Units feels consistent).
//
// Click routing mirrors the Units table:
//   - Exactly one device on the unit → jump to that device's detail page
//   - 0 or 2+ devices → /units/<id>
// Alarmed cards carry the same red left accent the device grid uses so
// trouble pops in a wall of healthy ones.
export function PropertyUnitCard({ unit, devices, propertyId, canWrite, onEdit, onDelete }: Props) {
  const navigate = useNavigate();
  const totalAlarms = devices.reduce((s, d) => s + (d.activeAlerts?.length ?? 0), 0);
  const hasAlarms = totalAlarms > 0;
  const primary = devices[0];
  const tx = primary?.latestTransmission;
  const isFresh = primary ? hasFreshTransmission(primary) : false;
  const reading = isFresh && tx ? (tx.computedWaterTotalization ?? tx.waterTotalization) : null;
  const readingUnit = formatUnitOfMeasureSuffix(tx?.computedUnitOfMeasure ?? tx?.unitOfMeasure);

  const clickTarget = devices.length === 1
    ? deviceDetailPath(devices[0].id, { kind: 'property', id: propertyId })
    : `/units/${unit.id}`;

  return (
    <button
      type="button"
      onClick={() => navigate(clickTarget)}
      className={cn(
        'group relative flex flex-col gap-3 rounded-xl border bg-white dark:bg-gray-800 p-4 text-left transition-all',
        'border-gray-200 dark:border-gray-700',
        'hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md hover:-translate-y-0.5',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:border-primary-500',
        hasAlarms && 'border-l-[3px] border-l-error-500 dark:border-l-error-500',
      )}
      aria-label={
        `${unit.unitNumber}, ${unit.buildingName}, ${devices.length} device${devices.length === 1 ? '' : 's'}` +
        (hasAlarms ? `, ${totalAlarms} active alarm${totalAlarms === 1 ? '' : 's'}` : '')
      }
    >
      {/* Header — unit identity. Status badge dropped per QA feedback
          (Occupied/Vacant didn't carry useful information on this
          surface). Edit/Delete affordances live in the hover overlay
          below so the default state stays quiet. */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <DoorOpen className="w-4 h-4 flex-shrink-0 text-gray-400" />
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight">
            {unit.unitNumber}
          </p>
        </div>
        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate" title={unit.buildingName}>
          {unit.buildingName}
        </p>
      </div>

      {/* Body — device summary. Empty unit gets an explicit caption so
          the card doesn't read as "broken / loading". */}
      <div className="flex items-end justify-between gap-3 mt-auto min-w-0">
        <div className="min-w-0">
          {devices.length === 0 ? (
            <p className="text-xs italic text-gray-400">No devices</p>
          ) : (
            <>
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  aria-hidden="true"
                  className={cn(
                    'inline-block w-2 h-2 rounded-full flex-shrink-0',
                    hasAlarms ? 'bg-error-500' : isFresh ? 'bg-success-500' : 'bg-gray-400',
                  )}
                />
                <span className="font-mono text-xs truncate">{primary.serialNumber}</span>
                {devices.length > 1 && (
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0">
                    +{devices.length - 1}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">
                {deviceTypeLabel(primary)}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                {reading != null
                  ? `${reading.toLocaleString()} ${readingUnit}`
                  : primary?.lastSeenAt
                    ? `Last seen ${formatRelativeTime(primary.lastSeenAt)}`
                    : 'Awaiting uplink'}
              </p>
            </>
          )}
        </div>
        {hasAlarms && (
          <Badge variant="error" size="sm" className="flex-shrink-0">
            <AlertTriangle className="w-3 h-3" />
            {totalAlarms}
          </Badge>
        )}
      </div>

      {/* Hover-revealed action affordances. Top-right corner overlay so
          they don't compete with the status badge. The stopPropagation
          on each handler keeps the card click from also firing. */}
      {canWrite && (
        <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(unit); }}
            className="p-1 rounded bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 shadow-sm"
            title="Edit unit"
          >
            <Edit2 className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(unit); }}
            className="p-1 rounded bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm hover:bg-error-100 dark:hover:bg-error-900/30 text-gray-500 hover:text-error-600 dark:hover:text-error-400 shadow-sm"
            title="Delete unit"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </button>
  );
}
